import type { DraggableId, Edit, Placement } from "./types";

// Хранилище правок. Правила (GDD 4.5):
//  • один предмет — не более одной правки на момент; новая заменяет старую;
//  • правки хранятся только с моментом ≤ текущего;
//  • откат ниже момента правки удаляет её (предмет улетает на прежнее место);
//  • правка на нуле откатом не отменяется (ниже нуля мир не идёт).

type Listener = (edits: Edit[]) => void;

export class EditStore {
  private edits: Edit[] = [];
  private listeners = new Set<Listener>();

  list(): Edit[] {
    return this.edits.slice();
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    const snapshot = this.list();
    for (const fn of this.listeners) fn(snapshot);
  }

  /** Создать или заменить правку предмета на данном моменте. */
  set(objectId: DraggableId, tick: number, placement: Placement): Edit {
    const t = Math.max(0, Math.round(tick));
    this.edits = this.edits.filter((e) => !(e.objectId === objectId && e.tick === t));
    const edit: Edit = { objectId, tick: t, placement };
    this.edits.push(edit);
    this.edits.sort((a, b) => a.tick - b.tick);
    this.emit();
    return edit;
  }

  /**
   * Откат к моменту k: удалить все правки с tick > k (кроме tick === 0).
   * Возвращает удалённые правки — их предметы аниматор проводит «назад».
   */
  rollbackTo(k: number): Edit[] {
    const removed = this.edits.filter((e) => e.tick > k && e.tick > 0);
    if (removed.length === 0) return [];
    this.edits = this.edits.filter((e) => !(e.tick > k && e.tick > 0));
    this.emit();
    return removed;
  }

  reset() {
    this.edits = [];
    this.emit();
  }
}
